/**
 * letterbox — who gets mail today, and what the traveler has already read.
 *
 * Pure: no DOM, no Phaser, no fetch. The UI shell (LetterboxUi.ts) supplies
 * the day number, the content list and a Storage, and this module answers.
 *
 * Design constraints (docs/GDD-v2-softening.md pillars, doctrine
 * docs/design/ui-c5-indigo-ink.md):
 *  - At most ONE letter arrives per day, ever. Mail is not a feed.
 *  - Catch-up after an absence delivers one letter, not a backlog: coming back
 *    to eleven envelopes would be a chore, and a chore is the opposite of the
 *    thing. The rest keep, in order.
 *  - Authored order is delivery order among equally eligible letters, so the
 *    correspondence reads as a sequence rather than a shuffle.
 *
 * Naming note: this is `letterbox.ts` (rules) and the DOM shell is
 * `LetterboxUi.ts` rather than `Letterbox.ts` — two files differing only by
 * case collide on case-insensitive filesystems (macOS/Windows checkouts).
 */

import type { Letter } from './letters.schema';
import { RealmId } from '@game/shared';

export const LETTERS_STORAGE_KEY = 'rpg-gibli:letters';

/** Bumped when the persisted shape changes; unknown versions reset to empty. */
export const LETTERS_STATE_VERSION = 1;

/** The slice of the Storage API this module needs (mirrors dayCount.ts). */
export interface LetterStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** Persisted letterbox state. Ids only — content always comes from JSON. */
export interface LetterboxState {
  /** Letters that have arrived in the box, oldest first. */
  delivered: string[];
  /** Letters the traveler has opened. Always a subset of `delivered`. */
  read: string[];
  /** Day each delivered letter arrived on — drives the hanko day stamp. */
  deliveredOn: Record<string, number>;
  /** Day number of the most recent delivery; 0 when nothing has arrived. */
  lastDeliveryDay: number;
}

export interface DeliveryContext {
  /** Current day number (see dayCount.ts). */
  day: number;
  /** Realm the traveler is in, when the client knows it. */
  realm?: RealmId;
}

export function emptyLetterboxState(): LetterboxState {
  return { delivered: [], read: [], deliveredOn: {}, lastDeliveryDay: 0 };
}

// ── persistence ────────────────────────────────────────────────────────────

function toIdList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  for (const entry of value) {
    if (typeof entry === 'string' && entry.length > 0 && !out.includes(entry)) {
      out.push(entry);
    }
  }
  return out;
}

/** Coerce any stored value into a valid state. Never throws. */
export function parseLetterboxState(raw: string | null | undefined): LetterboxState {
  if (raw === null || raw === undefined || raw === '') return emptyLetterboxState();
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return emptyLetterboxState();
  }
  if (typeof parsed !== 'object' || parsed === null) return emptyLetterboxState();

  const obj = parsed as Record<string, unknown>;
  if (obj.v !== LETTERS_STATE_VERSION) return emptyLetterboxState();

  const delivered = toIdList(obj.delivered);
  const read = toIdList(obj.read).filter((id) => delivered.includes(id));
  const dayRaw = obj.lastDeliveryDay;
  const lastDeliveryDay = toDay(dayRaw);
  const deliveredOn: Record<string, number> = {};
  const onRaw = obj.deliveredOn;
  if (typeof onRaw === 'object' && onRaw !== null) {
    for (const id of delivered) {
      const day = toDay((onRaw as Record<string, unknown>)[id]);
      if (day > 0) deliveredOn[id] = day;
    }
  }
  return { delivered, read, deliveredOn, lastDeliveryDay };
}

function toDay(raw: unknown): number {
  return typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 0;
}

export function serializeLetterboxState(state: LetterboxState): string {
  return JSON.stringify({
    v: LETTERS_STATE_VERSION,
    delivered: state.delivered,
    read: state.read,
    deliveredOn: state.deliveredOn,
    lastDeliveryDay: state.lastDeliveryDay,
  });
}

export function loadLetterboxState(storage: LetterStorage | null): LetterboxState {
  if (!storage) return emptyLetterboxState();
  try {
    return parseLetterboxState(storage.getItem(LETTERS_STORAGE_KEY));
  } catch {
    return emptyLetterboxState();
  }
}

export function saveLetterboxState(
  state: LetterboxState,
  storage: LetterStorage | null,
): void {
  try {
    storage?.setItem(LETTERS_STORAGE_KEY, serializeLetterboxState(state));
  } catch {
    // best-effort; a full or blocked Storage must not break the frame
  }
}

// ── delivery rules ─────────────────────────────────────────────────────────

/** True when the day/realm gate on a letter is satisfied right now. */
export function isEligible(letter: Letter, ctx: DeliveryContext): boolean {
  const cond = letter.condition;
  if (!cond) return true;
  if (cond.minDay !== undefined && ctx.day < cond.minDay) return false;
  if (cond.realm !== undefined && cond.realm !== ctx.realm) return false;
  return true;
}

/**
 * The letter that would arrive right now, or null. Does not mutate.
 *
 * Rules, in order: nothing arrives twice; nothing arrives on a day that
 * already had a delivery; among what is left, the earliest authored eligible
 * letter wins.
 */
export function nextDeliverable(
  letters: readonly Letter[],
  ctx: DeliveryContext,
  state: LetterboxState,
): Letter | null {
  if (!Number.isFinite(ctx.day) || ctx.day < 1) return null;
  if (state.lastDeliveryDay >= ctx.day) return null;
  for (const letter of letters) {
    if (state.delivered.includes(letter.id)) continue;
    if (isEligible(letter, ctx)) return letter;
  }
  return null;
}

export interface DeliveryResult {
  state: LetterboxState;
  /** The letter that just arrived, or null when the box stays quiet. */
  delivered: Letter | null;
}

/**
 * Apply today's delivery. Idempotent within a day: calling twice on the same
 * day (boot catch-up, then a DAY_CHANGED edge) delivers once.
 */
export function deliverDueLetter(
  letters: readonly Letter[],
  ctx: DeliveryContext,
  state: LetterboxState,
): DeliveryResult {
  const letter = nextDeliverable(letters, ctx, state);
  if (!letter) return { state, delivered: null };
  const day = Math.floor(ctx.day);
  return {
    state: {
      delivered: [...state.delivered, letter.id],
      read: [...state.read],
      deliveredOn: { ...state.deliveredOn, [letter.id]: day },
      lastDeliveryDay: day,
    },
    delivered: letter,
  };
}

/** Day a letter arrived on, or 0 when the box has forgotten (older saves). */
export function deliveredDay(state: LetterboxState, id: string): number {
  return state.deliveredOn[id] ?? 0;
}

// ── reading ────────────────────────────────────────────────────────────────

/** Mark a delivered letter as opened. Unknown or undelivered ids are ignored. */
export function markRead(state: LetterboxState, id: string): LetterboxState {
  if (!state.delivered.includes(id) || state.read.includes(id)) return state;
  return { ...state, read: [...state.read, id] };
}

/** Delivered-but-unopened letters, oldest first. */
export function unreadLetters(
  letters: readonly Letter[],
  state: LetterboxState,
): Letter[] {
  return orderedByDelivery(letters, state).filter((l) => !state.read.includes(l.id));
}

/** Opened letters, newest first — the archive stack reads top-down. */
export function archivedLetters(
  letters: readonly Letter[],
  state: LetterboxState,
): Letter[] {
  return orderedByDelivery(letters, state)
    .filter((l) => state.read.includes(l.id))
    .reverse();
}

/** True when the socket should show a sealed envelope. */
export function hasUnread(letters: readonly Letter[], state: LetterboxState): boolean {
  return unreadLetters(letters, state).length > 0;
}

function orderedByDelivery(
  letters: readonly Letter[],
  state: LetterboxState,
): Letter[] {
  const byId = new Map(letters.map((l) => [l.id, l]));
  const out: Letter[] = [];
  for (const id of state.delivered) {
    const letter = byId.get(id);
    if (letter) out.push(letter);
  }
  return out;
}

/** Paragraphs of a letter body, blank-line separated, trimmed and non-empty. */
export function bodyParagraphs(letter: Letter): string[] {
  return letter.body
    .split(/\n\s*\n/)
    .map((p) => p.replace(/\s*\n\s*/g, ' ').trim())
    .filter((p) => p.length > 0);
}
